import { useEffect, useMemo, useState } from 'react';
import type { CpqState, RoleCatalogRecord, UserCatalogRecord } from './types';

export interface AcceptanceIdentity {
  roleId: string;
  userId: string;
}

export interface AcceptanceIdentityPort {
  load(): AcceptanceIdentity | null;
  save(identity: AcceptanceIdentity): void;
  clear(): void;
}

export function acceptanceIdentityFromState(
  state: CpqState,
): AcceptanceIdentity {
  return assertAcceptanceIdentity(state, {
    roleId: state.currentRoleId,
    userId: state.currentActorUserId ?? '',
  });
}

export function assertAcceptanceIdentity(
  state: CpqState,
  identity: AcceptanceIdentity,
): AcceptanceIdentity {
  const role = state.catalog.roles.find(
    (candidate) =>
      candidate.id === identity.roleId && candidate.status === 'active',
  );
  const actor = state.catalog.users.find(
    (candidate) =>
      candidate.id === identity.userId &&
      candidate.status === 'active' &&
      candidate.roleId === role?.id &&
      candidate.role === role?.key,
  );
  if (!role || !actor) throw new Error('成员不属于所选角色、未知或已停用');
  return { roleId: role.id, userId: actor.id };
}

export function applyAcceptanceIdentity(
  state: CpqState,
  identity: AcceptanceIdentity,
): CpqState {
  const stable = assertAcceptanceIdentity(state, identity);
  const role = state.catalog.roles.find(
    (candidate) => candidate.id === stable.roleId,
  );
  if (!role) throw new Error('所选角色未知或已停用');
  return {
    ...state,
    currentRoleId: role.id,
    role: role.key,
    currentActorUserId: stable.userId,
  };
}

export interface AcceptanceIdentitySelector {
  roleId: string;
  userId: string | undefined;
  roles: RoleCatalogRecord[];
  users: UserCatalogRecord[];
  selectRole(roleId: string): void;
  selectUser(userId: string): void;
}

/**
 * Holds an uncommitted role choice outside the domain state. The domain and
 * tab identity are changed only after a real active member is selected.
 */
export function useAcceptanceIdentitySelector(
  state: CpqState,
  bind: (identity: AcceptanceIdentity) => void,
): AcceptanceIdentitySelector {
  const [pendingRoleId, setPendingRoleId] = useState(state.currentRoleId);

  useEffect(() => setPendingRoleId(state.currentRoleId), [state.currentRoleId]);

  const roles = useMemo(
    () => state.catalog.roles.filter((role) => role.status === 'active'),
    [state.catalog.roles],
  );
  const users = useMemo(
    () =>
      state.catalog.users.filter(
        (user) => user.status === 'active' && user.roleId === pendingRoleId,
      ),
    [pendingRoleId, state.catalog.users],
  );

  return {
    roleId: pendingRoleId,
    userId:
      pendingRoleId === state.currentRoleId
        ? state.currentActorUserId
        : undefined,
    roles,
    users,
    selectRole(roleId) {
      const role = roles.find((candidate) => candidate.id === roleId);
      if (!role) throw new Error('所选角色未知或已停用');
      setPendingRoleId(role.id);
    },
    selectUser(userId) {
      bind(assertAcceptanceIdentity(state, { roleId: pendingRoleId, userId }));
    },
  };
}
